import { Box, Button, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { isValidEmailAddress } from '../../../services/Utilities';
import { SignupResultLayout } from '../SignupResultLayout/SignupResultLayout';
import { signupDomainErrorStyles } from './SignupDomainError.styles';

export type SignupDomainErrorCode = 'homeschool_email_mismatch';

interface SignupDomainErrorProps {
  signupReturnPath?: string;
  errorCode?: SignupDomainErrorCode | null;
  inline?: {
    provider: 'email' | 'google' | 'canvas' | 'classlink' | 'clever' | 'schoology';
    schoolName: string;
    emailAddress: string;
    isWorking: boolean;
    failedEmailAddress: string;
    error: string;
    onEmailChange: (email: string) => void;
    onUseDifferentMethod: () => void;
    isDistrictAdmin?: boolean;
  };
}

export function SignupDomainError({inline, signupReturnPath = '/signup', errorCode}: SignupDomainErrorProps) {
  const theme = useTheme();
  const isHomeschoolMismatch = errorCode === 'homeschool_email_mismatch';
  const homeschoolGuidance = 'This email is not approved for this homeschool subscription token. Use an approved email address or contact Exploros Support.';

  if (!inline) {
    return (
      <SignupResultLayout title={isHomeschoolMismatch ? 'Use an approved homeschool email' : 'Use your official school email'}>
        <Typography sx={signupDomainErrorStyles.resultMessage(theme)}>
          {isHomeschoolMismatch ? homeschoolGuidance : 'Sign up with your school or district email. Personal email is supported only for an approved homeschool account.'}
        </Typography>
        <Box sx={signupDomainErrorStyles.resultActions()}>
          <Button href={signupReturnPath} color="success" variant="outlined">Return to signup</Button>
        </Box>
      </SignupResultLayout>
    );
  }

  const isEmail = inline.provider === 'email';
  const providerName = inline.provider === 'google'
    ? 'Google'
    : inline.provider === 'canvas'
      ? 'Canvas'
    : inline.provider === 'classlink'
      ? 'ClassLink'
      : inline.provider === 'schoology' ? 'Schoology' : 'Clever';
  const canRetryEmail = isEmail
    && isValidEmailAddress(inline.emailAddress)
    && inline.emailAddress.trim().toLowerCase() !== inline.failedEmailAddress.trim().toLowerCase();

  // Retry submits the parent Account form, which still owns school selection and consent.
  return (
    <Box component="section" aria-labelledby="email-error-heading" sx={signupDomainErrorStyles.inlineResult()}>
      <Typography id="email-error-heading" component="h2" sx={signupDomainErrorStyles.accountHeading()}>{isEmail ? 'Sign up with email' : `Sign up with ${providerName}`}</Typography>
      <Typography sx={signupDomainErrorStyles.sectionDescription(theme)}>{isHomeschoolMismatch ? isEmail ? 'Use an email address approved for your homeschool subscription token.' : `Use a ${providerName} account with an email address approved for your homeschool subscription token.` : inline.isDistrictAdmin ? 'Use the email address associated with your district administrator invitation.' : isEmail ? 'Use your official school or district email.' : `Use a ${providerName} account with your official school or district email.`}</Typography>
      {isEmail && <TextField type="email" required fullWidth error value={inline.emailAddress} disabled={inline.isWorking} onChange={(event) => inline.onEmailChange(event.target.value)} slotProps={{htmlInput: {'aria-label': 'Email address'}}} sx={signupDomainErrorStyles.input()} />}
      <Box role="alert" sx={signupDomainErrorStyles.inlineError(theme)}>
        <Box component="span" aria-hidden="true" sx={signupDomainErrorStyles.errorIcon(theme)}>!</Box>
        <Box>
          <Typography sx={signupDomainErrorStyles.inlineErrorTitle()}>{isEmail ? inline.isDistrictAdmin || isHomeschoolMismatch ? 'We couldn’t verify this email' : 'We couldn’t verify this school email' : `We couldn’t verify this ${providerName} account`}</Typography>
          <Typography sx={signupDomainErrorStyles.inlineErrorMessage(theme)}>{isHomeschoolMismatch ? homeschoolGuidance : inline.isDistrictAdmin ? `We couldn’t verify ${isEmail ? 'this email' : `the email associated with this ${providerName} account`} for district administrator signup. Use the email address associated with your invitation.` : isEmail ? `This email doesn’t match the information we have for ${inline.schoolName}. Check the address or use your school-issued email.` : `The email associated with this ${providerName} account doesn’t match the information we have for ${inline.schoolName}. Use a ${providerName} account with your school-issued email.`}</Typography>
        </Box>
      </Box>
      {isEmail && <Button type="submit" fullWidth variant="contained" disableElevation disabled={!canRetryEmail || inline.isWorking} sx={signupDomainErrorStyles.emailButton(theme)}>{inline.isWorking ? 'Trying email…' : 'Try another email'}</Button>}
      {inline.error && <Typography role="alert" sx={signupDomainErrorStyles.actionError(theme)}>{inline.error}</Typography>}
      <Button type="button" variant="text" disabled={inline.isWorking} onClick={inline.onUseDifferentMethod} sx={signupDomainErrorStyles.differentMethod(theme)}>Use a different sign-up method</Button>
    </Box>
  );
}
